import React, { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Text,
  useToast,
} from '@chakra-ui/react';
import { client } from '@php-beam/packages/frontend/src/api/client';
import { LeadQuoteInfo } from './types';

export default function TwoMinuteSection({
  leadId,
  quotes,
  defaults,
  onDone,
}: {
  leadId: number;
  quotes: LeadQuoteInfo[];
  defaults: Record<string, any>;
  onDone: () => void;
}) {
  const [selectedQuote, setSelectedQuote] = useState<number | ''>('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(defaults);
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  const simulate = async () => {
    if (!selectedQuote) return;
    setSubmitting(true);
    try {
      await client.post(`/devtools/documents/lead/${leadId}/two-minute`, {
        quote_id: selectedQuote,
        ...formData,
      });
      toast({
        title: 'Two Minute Signup simulated',
        status: 'success',
        duration: 3000,
      });
      onDone();
    } catch (err: any) {
      toast({
        title: 'Failed to simulate',
        description: err?.response?.data?.message || 'Unknown error',
        status: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const alreadySigned = quotes.filter((q) => q.two_minute_signup_id !== null);

  return (
    <Box p={3} borderWidth={1} borderRadius="md" borderColor="gray.200">
      <HStack mb={2}>
        <Text fontWeight="semibold" fontSize="sm">
          Two Minute Signup
        </Text>
        {alreadySigned.length > 0 && (
          <Badge colorScheme="green" fontSize="xs">
            {alreadySigned.length} signed
          </Badge>
        )}
      </HStack>

      {quotes.length === 0 ? (
        <Text fontSize="sm" color="gray.500">
          Create a quote first
        </Text>
      ) : (
        <>
          <HStack spacing={2} mb={2}>
            <Select
              size="sm"
              placeholder="Select quote"
              value={selectedQuote}
              onChange={(e) => setSelectedQuote(Number(e.target.value))}
              maxW="250px"
            >
              {quotes.map((q) => (
                <option key={q.id} value={q.id}>
                  #{q.id} — {q.theme} / {q.type}
                  {q.two_minute_signup_id ? ' (signed)' : ''}
                </option>
              ))}
            </Select>
            <Button
              size="sm"
              colorScheme="purple"
              onClick={simulate}
              isLoading={submitting}
              isDisabled={!selectedQuote}
            >
              Quick Simulate
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowForm(!showForm)}
            >
              {showForm ? 'Hide' : 'Edit'}
            </Button>
          </HStack>

          {showForm && (
            <TwoMinuteForm data={formData} onChange={setFormData} />
          )}
        </>
      )}
    </Box>
  );
}

function TwoMinuteForm({
  data,
  onChange,
}: {
  data: Record<string, any>;
  onChange: (d: Record<string, any>) => void;
}) {
  const set = (key: string, value: any) => onChange({ ...data, [key]: value });

  return (
    <SimpleGrid columns={2} spacing={2} mt={2}>
      <FormControl>
        <FormLabel fontSize="xs">FEIN</FormLabel>
        <Input size="sm" value={data.fein ?? ''} onChange={(e) => set('fein', e.target.value)} />
      </FormControl>
      <FormControl>
        <FormLabel fontSize="xs">Owner Name</FormLabel>
        <Input size="sm" value={data.owner_name ?? ''} onChange={(e) => set('owner_name', e.target.value)} />
      </FormControl>
      <FormControl>
        <FormLabel fontSize="xs">Business Phone</FormLabel>
        <Input size="sm" value={data.business_phone ?? ''} onChange={(e) => set('business_phone', e.target.value)} />
      </FormControl>
      <FormControl>
        <FormLabel fontSize="xs">Business Email</FormLabel>
        <Input size="sm" value={data.business_email ?? ''} onChange={(e) => set('business_email', e.target.value)} />
      </FormControl>
      <FormControl>
        <FormLabel fontSize="xs">Commercial %</FormLabel>
        <Input size="sm" type="number" value={data.commercial_percent ?? ''} onChange={(e) => set('commercial_percent', Number(e.target.value))} />
      </FormControl>
      <FormControl>
        <FormLabel fontSize="xs">Residential %</FormLabel>
        <Input size="sm" type="number" value={data.residential_percent ?? ''} onChange={(e) => set('residential_percent', Number(e.target.value))} />
      </FormControl>
    </SimpleGrid>
  );
}
